/**
 * ReasonListAdmin — an Admin list of titled reasons (Q/A reasons, membership
 * cancellation reasons): the corporate dashboard's reason tables for one
 * account, in the Lead Sources layout (see AdminList.tsx):
 *   - one Title column, sorted by title; search by title; Show deleted;
 *   - "Add reason" top right; a click on a live row opens it; Edit / Delete on
 *     a live row (Delete asks first), Restore on a deleted one;
 *   - the form is corp's: a single "Title *" ("Title is required").
 * Every reason is listed, with the count in the footer.
 *
 * On a Locked page the shared blocks drop "Add reason" and the row icons, and
 * a click on a row opens the reason read-only; the list also drops its
 * (then empty) Actions column, and the empty list stops inviting an add.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/services/apiClient';
import type { AdminReason, ReasonApi } from '@/services/reasons.service';
import { toast } from '@/store/toastStore';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from './AdminList';
import { AdminFormModal } from './AdminFormModal';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from './AdminRowActions';
import { DialogField, fieldClasses } from './DialogChrome';
import { useListPagination } from './useListPagination';
import { compareText, useListSort } from './useListSort';
import { useTrackedForm } from './useTrackedForm';

export interface ReasonListText {
  title: string;
  subtitle: string;
  /** Singular / plural, for the footer count and messages ("reason", "reasons"). */
  noun: [string, string];
  /** "Q/A reason" — used in toasts and the delete prompt. */
  thing: string;
  /** The search box's accessible name, e.g. "Search Q/A reasons". */
  searchLabel: string;
  emptyTitle: string;
  idPrefix: string;
}

const COLUMNS: AdminListColumn[] = [{ key: 'title', header: 'Title', sortable: true }, ACTIONS_COLUMN];
/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS: AdminListColumn[] = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function titleOf(r: AdminReason): string {
  return r.title.trim() || `Reason #${r.id}`;
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function ReasonListAdmin({ api, text }: { api: ReasonApi; text: ReasonListText }) {
  const locked = useAdminPageLevel() === 'locked';
  const [reasons, setReasons] = useState<AdminReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminReason | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminReason | null>(null);

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        setReasons(await api.list());
      } catch (err) {
        toast.error(getErrorMessage(err, `Failed to load ${text.noun[1]}`));
      } finally {
        setLoading(false);
      }
    },
    [api, text.noun],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      reasons
        .filter((r) => (showDeleted || r.active) && (query === '' || r.title.toLowerCase().includes(query)))
        .sort((a, b) => compareText(titleOf(a), titleOf(b)) || a.id - b.id),
    [reasons, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: 'title', compare: { title: (a, b) => compareText(titleOf(a), titleOf(b)) } });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { id } = deleting;
    try {
      await api.remove(id);
      setReasons((prev) => prev.map((r) => (r.id === id ? { ...r, active: false } : r)));
      toast.success(`${capitalise(text.thing)} deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, `The ${text.thing} could not be deleted.`));
    }
  }

  async function handleRestore(r: AdminReason) {
    try {
      await api.restore(r.id);
      setReasons((prev) => prev.map((x) => (x.id === r.id ? { ...x, active: true } : x)));
      toast.success(`${capitalise(text.thing)} restored`);
    } catch (err) {
      toast.error(getErrorMessage(err, `The ${text.thing} could not be restored.`));
    }
  }

  return (
    <AdminListPage
      title={text.title}
      subtitle={text.subtitle}
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add reason
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search reasons..."
          searchLabel={text.searchLabel}
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId={`${text.idPrefix}-show-deleted`}
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label={`Loading ${text.noun[1]}…`} />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? text.emptyTitle : `No ${text.noun[1]} yet`}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a reason to get started.'}
        />
      ) : (
        <>
          <AdminListTable
            columns={locked ? LOCKED_COLUMNS : COLUMNS}
            aria-label={text.title}
            sort={sorting.sort}
            onSortChange={sorting.onSortChange}
          >
            {pagination.pageItems.map((r) => {
              const deleted = !r.active;
              const title = titleOf(r);
              return (
                <tr
                  key={r.id}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(r)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="reason-title">
                      {title}
                    </AdminRowName>
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => void handleRestore(r)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${title}`} onClick={() => setEditing(r)} />
                            <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => setDeleting(r)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={text.noun} />
        </>
      )}

      {editing && (
        <ReasonFormModal
          key={editing === 'new' ? 'new' : editing.id}
          api={api}
          reason={editing === 'new' ? null : editing}
          idPrefix={text.idPrefix}
          thing={text.thing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${titleOf(deleting)}"?` : ''}
        message="It will be deleted. You can restore it from Show deleted."
        confirmLabel="Delete reason"
        variant="danger"
      />
    </AdminListPage>
  );
}

function ReasonFormModal({
  api,
  reason,
  idPrefix,
  thing,
  onClose,
  onSaved,
}: {
  api: ReasonApi;
  reason: AdminReason | null;
  idPrefix: string;
  thing: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = reason === null;
  const t = useTrackedForm(() => ({ title: reason?.title ?? '' }), { tracking: !isNew });
  const [saving, setSaving] = useState(false);
  const missing = t.form.title.trim() === '';

  async function save() {
    if (missing) return;
    setSaving(true);
    try {
      const title = t.form.title.trim();
      if (reason) await api.update(reason.id, title);
      else await api.create(title);
      toast.success(`${capitalise(thing)} ${isNew ? 'created' : 'updated'}`);
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, `The ${thing} could not be saved.`));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Reason' : 'Edit Reason'}
      isNew={isNew}
      dirty={isNew ? !missing : t.dirty}
      saving={saving}
      errors={missing ? ['Title is required'] : []}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="md"
    >
      <DialogField
        label="Title"
        htmlFor={`${idPrefix}-title`}
        required
        state={missing ? 'error' : 'none'}
        labelHint={missing ? '— required' : undefined}
        badge={t.badge('title', 'Title')}
      >
        <Input
          id={`${idPrefix}-title`}
          value={t.form.title}
          placeholder="Reason title"
          maxLength={255}
          onChange={(e) => t.patch({ title: e.target.value })}
          className={fieldClasses.input(missing ? 'error' : 'none')}
        />
      </DialogField>
    </AdminFormModal>
  );
}
